// frontend/src/pages/dashboard/Documentation.js
import React, { useState, useEffect, useMemo } from "react";
import {
    Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    Alert, CircularProgress, Box, Divider, Grid, Chip, useTheme,
    useMediaQuery, IconButton, Collapse, Stack, InputAdornment,
    Tabs, Tab, Tooltip, Accordion, AccordionSummary, AccordionDetails,
    List, ListItem, ListItemButton, ListItemIcon, ListItemText,
    Breadcrumbs, Link, Card, CardContent, CardHeader, Avatar, Button, useScrollTrigger
} from "@mui/material";
import {
    Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
    ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
    Phone as PhoneIcon, Email as EmailIcon, LocationOn as LocationIcon,
    Business as BusinessIcon, Receipt as ReceiptIcon, Person as PersonIcon,
    Home as HomeIcon, Assignment as AssignmentIcon, ArrowBack as BackIcon,
    Save as SaveIcon, Search as SearchIcon, FilterList as FilterIcon,
    Close as CloseIcon, Refresh as RefreshIcon, MenuBook as MenuBookIcon,
    Timeline as TimelineIcon, Speed as SpeedIcon, AttachMoney as AttachMoneyIcon,
    Event as EventIcon, Group as GroupIcon, Lightbulb as LightbulbIcon,
    Help as HelpIcon, CheckCircle as CheckCircleIcon,
    ArrowForward as ArrowForwardIcon, ContentCopy as ContentCopyIcon,
    Download as DownloadIcon, Print as PrintIcon
} from "@mui/icons-material";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { useAuth } from "../../context/AuthContext";



// 📚 Documentation Sections Data
const documentationSections = [
    {
        id: "phase1",
        title: "🚀 Phase 1: Getting Started",
        icon: <HomeIcon color="primary" />,
        content: {
            description: "What happens first when your company joins Syncro",
            steps: [
                {
                    title: "Company Registration",
                    details: "Admin visits the registration page, enters company details, email, timezone, and contact info. System generates a temporary password and a license key."
                },
                {
                    title: "Email Verification",
                    details: "Admin receives a verification email → clicks the link to activate the account. A second email arrives with login credentials (email + temp password + license key)."
                },
                {
                    title: "First Login",
                    details: "Go to the login screen → enter email, temporary password, and license key (required only once). System prompts you to change your password for security."
                },
                {
                    title: "Subscription & Trial",
                    details: "You start with a 7-day free trial (Freelancer package). Upgrade anytime via the Packages page to unlock more users, projects, tasks, and storage."
                }
            ]
        }
    },
    {
        id: "phase2",
        title: "🏗️ Phase 2: Workspace Setup",
        icon: <AssignmentIcon color="success" />,
        content: {
            description: "Follow this sequence to prepare your company for daily work",
            table: {
                headers: ["Step", "Action", "Where to Find It"],
                rows: [
                    ["1️⃣", "Add Team Members", "Team → Add Member → Enter email & assign role"],
                    ["2️⃣", "Configure Roles & Permissions", "Team → Add Role → Select permissions (optional)"],
                    ["3️⃣", "Add Clients", "Clients → Add Client → Fill contact & billing details"],
                    ["4️⃣", "Create Your First Project", "Projects → Add Project → Set dates, billing model, assign team/client"],
                    ["5️⃣", "Set Project Statuses", "Status → Choose Task or Project type → Add/edit workflow stages"]
                ]
            },
            tip: "💡 Always add team members before creating projects so you can assign them during project creation."
        }
    },
    {
        id: "phase3",
        title: "📋 Phase 3: Daily Operations",
        icon: <TimelineIcon color="info" />,
        content: {
            description: "How your team will use the app every day",
            steps: [
                {
                    title: "🔹 Create & Assign Tasks",
                    details: "Open a project → Click + Add Task. Fill: Title, Description, Priority, Due Date, Story Points (optional). Assign to team members → Select a column/status (e.g., To Do). Upload attachments if needed."
                },
                {
                    title: "🔹 Work on Tasks",
                    details: "Open the task → Update status as work progresses (To Do → In Progress → In Review → Done). Add Comments: Type @Name to mention teammates. Paste images directly with Ctrl+V or attach files. Time Tracking: Click Start Timer when beginning work → Pause for breaks → Stop when done."
                },
                {
                    title: "🔹 Monitor Progress",
                    details: "View the Kanban Board (View Board) to drag & drop tasks between columns. Check the Dashboard for real-time stats: active projects, overdue tasks, completion rates. Notifications will appear in the top bell icon for assignments, comments, and approvals."
                }
            ]
        }
    },
    {
        id: "phase4",
        title: "🏃 Phase 4: Agile & Sprint Management",
        icon: <SpeedIcon color="warning" />,
        content: {
            description: "For teams using Scrum/Agile workflows",
            steps: [
                {
                    title: "📦 Backlog",
                    details: "Go to Projects → Backlog. Add future tasks → Set priority & story points → Drag to reorder by importance. Overdue tasks automatically move here."
                },
                {
                    title: "🎯 Sprint Planning",
                    details: "Select tasks from Backlog → Choose Move to Sprint → Pick an active/planning sprint → Set a new due date."
                },
                {
                    title: "▶️ Run the Sprint",
                    details: "Start Sprint → Team moves tasks across the board daily. Complete Sprint → System calculates velocity (story points completed)."
                },
                {
                    title: "🔄 Review & Repeat",
                    details: "Check sprint progress bars, velocity stats, and burndown charts. Start a new sprint and pull in the next set of backlog items."
                }
            ]
        }
    },
    {
        id: "phase5",
        title: "⏱️ Phase 5: Time Tracking & Performance",
        icon: <SpeedIcon color="error" />,
        content: {
            description: "How productivity is measured",
            steps: [
                {
                    title: "⏱️ Live Timer",
                    details: "Tracks hours per task automatically. Supports pause/resume sessions."
                },
                {
                    title: "📝 Manual Entry",
                    details: "If you forget to start the timer, log hours manually with start/end times."
                },
                {
                    title: "📊 Performance Dashboard",
                    details: "System auto-calculates: Tasks completed, On-time delivery %, Hours logged, Performance Score (0-100). View personal or team KPIs under KPI Performance → Filter by Month/Year."
                },
                {
                    title: "✅ KPI Approval (Optional)",
                    details: "Members submit KPI targets → Admin reviews in KPI Approvals → Approve/Reject with feedback. Approved KPIs reflect in performance reports."
                }
            ]
        }
    },
    {
        id: "phase6",
        title: "💰 Phase 6: Billing & Client Management",
        icon: <AttachMoneyIcon color="success" />,
        content: {
            description: "How projects turn into invoices",
            billingModels: [
                { model: "Hourly", howItWorks: "Bill = (Logged Hours × Hourly Rate)" },
                { model: "Fixed", howItWorks: "Bill = Agreed fixed price (invoiced on project completion)" },
                { model: "Mixed", howItWorks: "Bill = Fixed Base + Hourly Overage (invoiced on completion)" },
                { model: "Periodic", howItWorks: "Auto-invoices generated at each period end (Monthly/Quarterly/Half-Yearly/Yearly)" }
            ],
            invoiceWorkflow: [
                { step: "📄 Generation", detail: "Invoices auto-create based on project billing model & completion status." },
                { step: "👁️ Review", detail: "Go to Billing → View all invoices per client/project → Check amounts, due dates, status." },
                { step: "💵 Record Payment", detail: "Click Pay on an invoice → Enter received amount → System marks as Paid or Partial." },
                { step: "📧 Send to Client", detail: "Click 📧 → Invoice email is sent directly to the client's registered email." },
                { step: "⚠️ Track Overdues", detail: "Overdue invoices are highlighted. Dashboard shows total due, paid, and overdue amounts." }
            ]
        }
    },
    {
        id: "phase7",
        title: "🔔 Phase 7: Calendar & Notifications",
        icon: <EventIcon color="primary" />,
        content: {
            description: "Staying in sync without missing deadlines",
            steps: [
                {
                    title: "📅 Calendar View",
                    details: "Aggregates project start/end dates, task deadlines, and sprint timelines. Color-coded by priority."
                },
                {
                    title: "🔄 Real-Time Updates",
                    details: "The system refreshes automatically in the background. New comments, task updates, and assignments appear without manual refresh."
                },
                {
                    title: "🔔 Notification Center",
                    details: "Bell icon shows recent activity. Click any notification to jump directly to the related project, task, or invoice. Mark as read or clear all."
                },
                {
                    title: "📧 Email Alerts",
                    details: "Critical updates (assignments, mentions, KPI approvals, invoice reminders) are also emailed. Toggle preferences in Profile → Notifications."
                }
            ]
        }
    }
];

// 👥 Role-Based Access Data
const roleBasedGuide = [
    {
        role: "🟦 System Administrator",
        canDo: "Full access: Manage team, roles, projects, billing, subscriptions, settings, approve KPIs, delete resources",
        restricted: "Nothing (except system core config)"
    },
    {
        role: "🟨 Manager",
        canDo: "Create/edit assigned projects & tasks, view team performance, manage sprints/backlog, comment & track time",
        restricted: "Billing, subscription upgrades, global settings"
    },
    {
        role: "🟩 Member",
        canDo: "View assigned tasks/projects, update status, log time, add comments/attachments, view personal KPIs",
        restricted: "Creating projects, managing team, viewing billing"
    },
    {
        role: "🟥 Client",
        canDo: "View linked projects & tasks (read-only), comment on deliverables, download attachments, receive invoices",
        restricted: "Accessing team, billing, settings, or other clients' data"
    }
];

// 💡 Best Practices Data
const bestPractices = [
    { icon: <CheckCircleIcon color="success" />, title: "Keep Statuses Updated", detail: "Moving tasks accurately ensures correct burndown charts, KPI tracking, and billing." },
    { icon: <CheckCircleIcon color="success" />, title: "Use Time Tracking", detail: "Essential for Hourly billing and accurate performance scoring." },
    { icon: <CheckCircleIcon color="success" />, title: "Leverage Filters", detail: "Every list (Projects, Tasks, Clients) supports search, status filters, and sorting. Use them to find work fast." },
    { icon: <CheckCircleIcon color="success" />, title: "Set Realistic Due Dates", detail: "The system automatically moves overdue tasks to the Backlog for reprioritization." },
    { icon: <CheckCircleIcon color="success" />, title: "Check Notifications Daily", detail: "Stay aligned with team changes, approvals, and deadlines." },
    { icon: <CheckCircleIcon color="success" />, title: "Secure Your Account", detail: "Change your temporary password immediately. Use strong passwords and update them periodically." }
];

// Helper component for StorageIcon (since not imported above)
const StorageIcon = () => <span>💾</span>;

// 🆘 Support Data
const supportItems = [
    { icon: <ReceiptIcon />, title: "Upgrade Package", detail: "Packages → Select plan → Confirm (redirects to payment/upgrade flow)" },
    { icon: <PersonIcon />, title: "Profile Settings", detail: "Profile → Update name, email, avatar, job title, skills, AI analysis toggle, password" },
    { icon: <BusinessIcon />, title: "Company Settings (Admin)", detail: "Profile → Update company name, address, phone, timezone (affects all date displays)" },
    { icon: <StorageIcon />, title: "Storage Limits", detail: "If upload fails, check your package limits. Delete old attachments or upgrade to continue." },
    { icon: <HelpIcon />, title: "Need Help?", detail: "Contact your System Administrator or reach out to support via the help center." }
];

const DocumentationPage = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    const { user: authUser } = useAuth();

    const [searchQuery, setSearchQuery] = useState("");
    const [activeSection, setActiveSection] = useState("phase1");
    const [expandedPanel, setExpandedPanel] = useState("phase1");
    const [copiedCode, setCopiedCode] = useState(null);
    const [printMode, setPrintMode] = useState(false);

    // Filter sections based on search
    const filteredSections = useMemo(() => {
        if (!searchQuery) return documentationSections;
        const query = searchQuery.toLowerCase();
        return documentationSections.filter(section =>
            section.title.toLowerCase().includes(query) ||
            section.content.description.toLowerCase().includes(query) ||
            section.content.steps?.some(step =>
                step.title.toLowerCase().includes(query) ||
                step.details.toLowerCase().includes(query)
            )
        );
    }, [searchQuery]);

    // Handle section click for navigation
    const handleSectionClick = (sectionId) => {
        setActiveSection(sectionId);
        setExpandedPanel(sectionId);
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    // Handle accordion change
    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpandedPanel(isExpanded ? panel : false);
    };

    // Copy code to clipboard
    const handleCopyCode = (code, id) => {
        navigator.clipboard.writeText(code);
        setCopiedCode(id);
        setTimeout(() => setCopiedCode(null), 2000);
    };

    // Print documentation
    const handlePrint = () => {
        window.print();
    };

    // Tab handling for different content types
    const [contentTab, setContentTab] = useState(0);

    const trigger = useScrollTrigger({
        disableHysteresis: true,
        threshold: 88,
    });

    return (
        <DashboardLayout>
            <Box sx={{
                p: { xs: 2, md: 4 },
            }}>

                {/* 🎯 Header Section */}
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 3, md: 4 },
                        mb: 4,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        color: "white",
                        borderRadius: 3,
                        position: "relative",
                        overflow: "hidden",
                        '@media print': { display: "none" }
                    }}
                >
                    <Box sx={{ position: "relative", zIndex: 1 }}>
                        <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                            <Avatar sx={{ bgcolor: "white", color: theme.palette.primary.main }}>
                                <MenuBookIcon />
                            </Avatar>
                            <Box>
                                <Typography variant="h4" fontWeight="bold">
                                    Syncro SaaS Project Management
                                </Typography>
                                <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                                    End-User Workflow & Application Guide
                                </Typography>
                            </Box>
                        </Stack>

                    </Box>

                    {/* Decorative Elements */}
                    <Box sx={{
                        position: "absolute",
                        right: -50,
                        top: -50,
                        width: 200,
                        height: 200,
                        borderRadius: "50%",
                        bgcolor: "rgba(255,255,255,0.1)"
                    }} />
                    <Box sx={{
                        position: "absolute",
                        left: -30,
                        bottom: -30,
                        width: 150,
                        height: 150,
                        borderRadius: "50%",
                        bgcolor: "rgba(255,255,255,0.05)"
                    }} />
                </Paper>

                {/* 🔍 Search & Navigation */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    {/* Sidebar Navigation - Desktop */}
                    {!isMobile && (
                        <Grid item size={{ xs: 12, md: 3, sm: 12 }} sx={{ display: { sm: 'none', md: 'block' } }}>
                            <Paper elevation={trigger ? 3 : 0} 
                                sx={{ 
                                    p: 2, 
                                    position: { xs: 'relative', md: trigger ? 'fixed' : 'relative', sm: 'relative' }, 
                                    top: { xs: 'auto', md: trigger ? 88 : 'auto' }, 
                                    width: { xs: '100%', md: trigger ? 'calc(22% - 30px)' : 'auto' }, 
                                    left: { md: trigger ? '270px' : 'auto' }, 
                                    height: 'fit-content', 
                                    maxHeight: 'calc(100vh - 104px)', 
                                    zIndex: 1100, 
                                    borderRadius: 2, 
                                    border: `1px solid ${theme.palette.divider}`, 
                                    transition: 'box-shadow 0.2s ease, position 0.2s ease' 
                                }}>
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                                    <FilterIcon fontSize="small" /> Table of Contents
                                </Typography>

                                {/* Search */}
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Search documentation..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" color="action" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: searchQuery && (
                                            <InputAdornment position="end">
                                                <IconButton size="small" onClick={() => setSearchQuery("")}>
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                    sx={{ mb: 2 }}
                                />

                                {/* Navigation List */}
                                <List dense sx={{ maxHeight: "60vh", overflow: "auto" }}>
                                    {filteredSections.map((section) => (
                                        <ListItem key={section.id} disablePadding>
                                            <ListItemButton
                                                selected={activeSection === section.id}
                                                onClick={() => handleSectionClick(section.id)}
                                                sx={{
                                                    borderRadius: 1,
                                                    my: 0.5,
                                                    '&.Mui-selected': {
                                                        bgcolor: `${theme.palette.primary.main}15`,
                                                        '&:hover': { bgcolor: `${theme.palette.primary.main}25` }
                                                    }
                                                }}
                                            >
                                                <ListItemIcon sx={{ minWidth: 36 }}>
                                                    {section.icon}
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={section.title}
                                                    primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: activeSection === section.id ? 600 : 400 }}
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    ))}
                                    <Divider sx={{ my: 1 }} />
                                    <ListItem disablePadding>
                                        <ListItemButton onClick={() => handleSectionClick("roles")} sx={{ borderRadius: 1, my: 0.5 }}>
                                            <ListItemIcon sx={{ minWidth: 36 }}><GroupIcon color="info" /></ListItemIcon>
                                            <ListItemText primary="👥 Role-Based Guide" primaryTypographyProps={{ fontSize: "0.875rem" }} />
                                        </ListItemButton>
                                    </ListItem>
                                    <ListItem disablePadding>
                                        <ListItemButton onClick={() => handleSectionClick("best-practices")} sx={{ borderRadius: 1, my: 0.5 }}>
                                            <ListItemIcon sx={{ minWidth: 36 }}><LightbulbIcon color="warning" /></ListItemIcon>
                                            <ListItemText primary="💡 Best Practices" primaryTypographyProps={{ fontSize: "0.875rem" }} />
                                        </ListItemButton>
                                    </ListItem>
                                    <ListItem disablePadding>
                                        <ListItemButton onClick={() => handleSectionClick("support")} sx={{ borderRadius: 1, my: 0.5 }}>
                                            <ListItemIcon sx={{ minWidth: 36 }}><HelpIcon color="error" /></ListItemIcon>
                                            <ListItemText primary="🆘 Support" primaryTypographyProps={{ fontSize: "0.875rem" }} />
                                        </ListItemButton>
                                    </ListItem>
                                </List>
                            </Paper>
                        </Grid>
                    )}

                    {/* Main Content */}
                    <Grid item size={{ xs: 12, md: 9 }}>

                        {/* Mobile Search */}
                        {isMobile && (
                            <TextField
                                fullWidth
                                placeholder="Search documentation..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon color="action" />
                                        </InputAdornment>
                                    )
                                }}
                                sx={{ mb: 3 }}
                            />
                        )}

                        {/* Documentation Sections */}
                        {filteredSections.map((section) => (
                            <Accordion
                                key={section.id}
                                id={section.id}
                                expanded={expandedPanel === section.id}
                                onChange={handleAccordionChange(section.id)}
                                elevation={0}
                                sx={{
                                    mb: 2,
                                    borderRadius: 2,
                                    border: `1px solid ${theme.palette.divider}`,
                                    '&:before': { display: "none" },
                                    '&.Mui-expanded': {
                                        border: `2px solid ${theme.palette.primary.main}`,
                                        boxShadow: `0 4px 20px ${theme.palette.primary.main}10`
                                    }
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                        bgcolor: expandedPanel === section.id ? `${theme.palette.primary.main}08` : "transparent",
                                        borderRadius: 2,
                                        '&:hover': { bgcolor: `${theme.palette.primary.main}05` }
                                    }}
                                >
                                    <Stack direction="row" spacing={2} alignItems="center" width="100%">
                                        {section.icon}
                                        <Typography fontWeight="600">{section.title}</Typography>
                                    </Stack>
                                </AccordionSummary>

                                <AccordionDetails>
                                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                                        {section.content.description}
                                    </Typography>

                                    {/* Steps List */}
                                    {section.content.steps && (
                                        <Stack spacing={3}>
                                            {section.content.steps.map((step, index) => (
                                                <Paper
                                                    key={index}
                                                    elevation={0}
                                                    sx={{
                                                        p: 2.5,
                                                        borderRadius: 2,
                                                        bgcolor: `${theme.palette.primary.main}05`,
                                                        borderLeft: `4px solid ${theme.palette.primary.main}`
                                                    }}
                                                >
                                                    <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                                                        {step.title}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
                                                        {step.details}
                                                    </Typography>
                                                </Paper>
                                            ))}
                                        </Stack>
                                    )}

                                    {/* Table Content */}
                                    {section.content.table && (
                                        <Box sx={{ overflowX: "auto", mb: 2 }}>
                                            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2 }}>
                                                <Table size="small">
                                                    <TableHead sx={{ bgcolor: `${theme.palette.primary.main}10` }}>
                                                        <TableRow>
                                                            {section.content.table.headers.map((header, idx) => (
                                                                <TableCell key={idx} sx={{ fontWeight: 600 }}>
                                                                    {header}
                                                                </TableCell>
                                                            ))}
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {section.content.table.rows.map((row, idx) => (
                                                            <TableRow key={idx} hover>
                                                                {row.map((cell, cellIdx) => (
                                                                    <TableCell key={cellIdx}>{cell}</TableCell>
                                                                ))}
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Box>
                                    )}

                                    {/* Billing Models Table */}
                                    {section.content.billingModels && (
                                        <Box sx={{ mb: 3 }}>
                                            <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                                                🔹 Billing Models Supported
                                            </Typography>
                                            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2 }}>
                                                <Table size="small">
                                                    <TableHead sx={{ bgcolor: `${theme.palette.success.main}10` }}>
                                                        <TableRow>
                                                            <TableCell sx={{ fontWeight: 600 }}>Model</TableCell>
                                                            <TableCell sx={{ fontWeight: 600 }}>How It Works</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {section.content.billingModels.map((model, idx) => (
                                                            <TableRow key={idx} hover>
                                                                <TableCell>
                                                                    <Chip
                                                                        label={model.model}
                                                                        size="small"
                                                                        color="success"
                                                                        variant="outlined"
                                                                        sx={{ fontWeight: 500 }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell>{model.howItWorks}</TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Box>
                                    )}

                                    {/* Invoice Workflow */}
                                    {section.content.invoiceWorkflow && (
                                        <Stack spacing={2}>
                                            <Typography variant="subtitle2" fontWeight="600">
                                                🔹 Invoice Workflow
                                            </Typography>
                                            {section.content.invoiceWorkflow.map((item, idx) => (
                                                <Stack key={idx} direction="row" spacing={2} alignItems="flex-start">
                                                    <Chip
                                                        label={item.step}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: `${theme.palette.warning.main}15`,
                                                            color: theme.palette.warning.dark,
                                                            fontWeight: 500,
                                                            flexShrink: 0
                                                        }}
                                                    />
                                                    <Typography variant="body2">{item.detail}</Typography>
                                                </Stack>
                                            ))}
                                        </Stack>
                                    )}

                                    {/* Tip Box */}
                                    {section.content.tip && (
                                        <Alert
                                            icon={<LightbulbIcon />}
                                            severity="info"
                                            sx={{ mt: 3, borderRadius: 2 }}
                                        >
                                            {section.content.tip}
                                        </Alert>
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        ))}

                        {/* 👥 Role-Based Guide Section */}
                        <Box id="roles" sx={{ scrollMarginTop: 100 }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    mb: 3,
                                    borderRadius: 2,
                                    border: `1px solid ${theme.palette.divider}`,
                                    bgcolor: `${theme.palette.info.main}05`
                                }}
                            >
                                <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                                    <Avatar sx={{ bgcolor: theme.palette.info.main, color: "white" }}>
                                        <GroupIcon />
                                    </Avatar>
                                    <Typography variant="h6" fontWeight="600">
                                        👥 Role-Based Quick Guide
                                    </Typography>
                                </Stack>

                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: `${theme.palette.info.main}15` }}>
                                                <TableCell sx={{ fontWeight: 600, width: "30%" }}>Role</TableCell>
                                                <TableCell sx={{ fontWeight: 600, width: "40%" }}>What You Can Do</TableCell>
                                                <TableCell sx={{ fontWeight: 600, width: "30%" }}>Restricted From</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {roleBasedGuide.map((role, idx) => (
                                                <TableRow key={idx} hover>
                                                    <TableCell>
                                                        <Chip
                                                            label={role.role}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: "transparent",
                                                                fontWeight: 600,
                                                                border: `1px solid ${theme.palette.divider}`
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">{role.canDo}</Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {role.restricted}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </Box>

                        {/* 💡 Best Practices Section */}
                        <Box id="best-practices" sx={{ scrollMarginTop: 100 }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    mb: 3,
                                    borderRadius: 2,
                                    border: `1px solid ${theme.palette.divider}`,
                                    bgcolor: `${theme.palette.warning.main}05`
                                }}
                            >
                                <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                                    <Avatar sx={{ bgcolor: theme.palette.warning.main, color: "white" }}>
                                        <LightbulbIcon />
                                    </Avatar>
                                    <Typography variant="h6" fontWeight="600">
                                        💡 Best Practices & Tips
                                    </Typography>
                                </Stack>

                                <Grid container spacing={2}>
                                    {bestPractices.map((practice, idx) => (
                                        <Grid item size={{ xs: 12, sm: 6 }} key={idx}>
                                            <Stack direction="row" spacing={2} alignItems="flex-start">
                                                <Box sx={{ mt: 0.5 }}>{practice.icon}</Box>
                                                <Box>
                                                    <Typography variant="subtitle2" fontWeight="600">
                                                        {practice.title}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {practice.detail}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Paper>
                        </Box>

                        {/* 🆘 Support Section */}
                        <Box id="support" sx={{ scrollMarginTop: 100 }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    mb: 3,
                                    borderRadius: 2,
                                    border: `1px solid ${theme.palette.divider}`,
                                    bgcolor: `${theme.palette.error.main}05`
                                }}
                            >
                                <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                                    <Avatar sx={{ bgcolor: theme.palette.error.main, color: "white" }}>
                                        <HelpIcon />
                                    </Avatar>
                                    <Typography variant="h6" fontWeight="600">
                                        🆘 Support & Account Management
                                    </Typography>
                                </Stack>

                                <Stack spacing={2}>
                                    {supportItems.map((item, idx) => (
                                        <Stack key={idx} direction="row" spacing={2} alignItems="flex-start">
                                            <Box sx={{ color: theme.palette.text.secondary, mt: 0.3 }}>{item.icon}</Box>
                                            <Box>
                                                <Typography variant="subtitle2" fontWeight="600">
                                                    {item.title}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {item.detail}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    ))}
                                </Stack>
                            </Paper>
                        </Box>

                        {/* Footer Note */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                borderRadius: 2,
                                bgcolor: theme.palette.background.default,
                                border: `1px dashed ${theme.palette.divider}`,
                                textAlign: "center"
                            }}
                        >
                            <Typography variant="body2" color="text.secondary">
                                📚 Documentation last updated: {new Date().toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                })}
                            </Typography>
                            <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 1 }}>
                                Need more help? Contact your System Administrator or visit the Help Center.
                            </Typography>
                        </Paper>

                    </Grid>
                </Grid>

            </Box>
        </DashboardLayout>
    );
};

export default DocumentationPage;